import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PlayCircle, X, Users } from "lucide-react";

function ytId(url = "") {
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? m[1] : null;
}

function fmt(n) {
  if (n == null) return null;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(n);
}

export default function EditorBytesStrip() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [channel, setChannel] = useState(null);

  useEffect(() => {
    api.get("/editor-bytes", { params: { limit: 12 } })
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoaded(true));
    api.get("/youtube/channel-stats").then((r) => setChannel(r.data)).catch(() => {});
  }, []);

  const channelUrl = channel?.url || "https://www.youtube.com/@cgIndiannews21?sub_confirmation=1";
  const subsPill = channel?.subscribers != null ? (
    <span className="inline-flex items-center gap-1 bg-white/15 text-white text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 border border-white/25">
      <Users className="w-3 h-3"/> {fmt(channel.subscribers)} Subs
    </span>
  ) : null;

  if (!loaded) return null;

  // Empty state: show YouTube channel subscribe CTA so the section is never dead.
  if (items.length === 0) {
    return (
      <section className="mb-8 rounded-xl overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] p-5 shadow-lg border border-red-500/30" data-testid="editor-bytes-empty">
        <div className="flex flex-col md:flex-row items-center gap-5">
          <div className="flex items-center gap-3 shrink-0">
            <div className="w-14 h-14 rounded-xl bg-[#FF0000] flex items-center justify-center shadow-lg">
              <PlayCircle className="w-8 h-8 text-white"/>
            </div>
            <div>
              <div className="uppercase tracking-[0.22em] text-[10px] text-red-300 font-black">YouTube Channel</div>
              <h3 className="font-devnagri-heading text-xl md:text-2xl font-black text-white leading-tight">CG Indian News 21</h3>
              <div className="text-slate-400 text-xs mt-0.5">प्रधान संपादक JAKIR MOMIN की वीडियो रिपोर्ट</div>
            </div>
          </div>
          <p className="text-slate-300 text-sm leading-relaxed md:flex-1">
            🔴 <b className="text-red-300">LIVE</b> — हमारे YouTube चैनल पर छत्तीसगढ़ की ताज़ा वीडियो खबरें देखें। Subscribe करके bell आइकन दबाएं ताकि हर नई रिपोर्ट का notification मिले।
          </p>
          <a
            href={channelUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 inline-flex items-center gap-2 bg-[#FF0000] hover:bg-[#CC0000] text-white font-black text-sm px-5 py-3 rounded-md shadow-lg transition"
            data-testid="youtube-subscribe-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M23.5 6.2c-.3-1-1-1.8-2-2C19.6 3.7 12 3.7 12 3.7s-7.6 0-9.5.5c-1 .3-1.7 1-2 2C0 8.1 0 12 0 12s0 3.9.5 5.8c.3 1 1 1.7 2 2 1.9.5 9.5.5 9.5.5s7.6 0 9.5-.5c1-.3 1.7-1 2-2 .5-1.9.5-5.8.5-5.8s0-3.9-.5-5.8zM9.6 15.6V8.4l6.3 3.6-6.3 3.6z"/></svg>
            Subscribe
          </a>
        </div>
      </section>
    );
  }

  return (
    <section className="mb-12 bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-xl p-6 shadow-lg" data-testid="editor-bytes-strip">
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="w-10 h-10 rounded-md bg-gradient-to-br from-amber-400 to-amber-600 text-[#0F172A] flex items-center justify-center">
          <PlayCircle className="w-6 h-6"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="uppercase tracking-[0.22em] text-[10px] text-amber-300 font-black flex items-center gap-2">
            Editor's Video Bytes {subsPill}
          </div>
          <h3 className="font-devnagri-heading text-2xl font-black text-white leading-tight">प्रधान संपादक की ज़बानी</h3>
        </div>
        <a href={channelUrl} target="_blank" rel="noopener noreferrer"
          className="shrink-0 inline-flex items-center gap-1.5 bg-[#FF0000] hover:bg-[#CC0000] text-white font-black text-xs px-3 py-2 rounded-md shadow-lg transition"
          data-testid="strip-subscribe-btn">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M23.5 6.2c-.3-1-1-1.8-2-2C19.6 3.7 12 3.7 12 3.7s-7.6 0-9.5.5c-1 .3-1.7 1-2 2C0 8.1 0 12 0 12s0 3.9.5 5.8c.3 1 1 1.7 2 2 1.9.5 9.5.5 9.5.5s7.6 0 9.5-.5c1-.3 1.7-1 2-2 .5-1.9.5-5.8.5-5.8s0-3.9-.5-5.8zM9.6 15.6V8.4l6.3 3.6-6.3 3.6z"/></svg>
          Subscribe
        </a>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 -mx-2 px-2 snap-x">
        {items.map((v) => {
          const id = ytId(v.video_url);
          return (
            <button key={v.id} onClick={() => setActive(v)} className="shrink-0 w-64 snap-start text-left group" data-testid={`byte-thumb-${v.id}`}>
              <div className="relative aspect-video rounded-md overflow-hidden bg-black border border-white/10 group-hover:border-amber-400 transition">
                {v.thumbnail_url ? (
                  <img src={v.thumbnail_url} alt={v.title} className="w-full h-full object-cover"/>
                ) : <div className="w-full h-full bg-slate-800"/>}
                <div className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/10 transition">
                  <PlayCircle className="w-12 h-12 text-white drop-shadow-lg"/>
                </div>
              </div>
              <div className="mt-2 text-sm font-bold text-white line-clamp-2 group-hover:text-amber-300 transition">{v.title}</div>
              {v.description && <div className="text-[11px] text-slate-400 line-clamp-1 mt-0.5">{v.description}</div>}
              {id && <span className="hidden">{id}</span>}
            </button>
          );
        })}
      </div>

      {active && (
        <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={() => setActive(null)} data-testid="byte-modal">
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="absolute -top-10 right-0 flex items-center gap-3">
              <a
                href={active.video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 bg-[#FF0000] hover:bg-[#CC0000] text-white text-xs font-bold px-3 py-1.5 rounded-md transition"
                data-testid="byte-open-yt">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M23.5 6.2c-.3-1-1-1.8-2-2C19.6 3.7 12 3.7 12 3.7s-7.6 0-9.5.5c-1 .3-1.7 1-2 2C0 8.1 0 12 0 12s0 3.9.5 5.8c.3 1 1 1.7 2 2 1.9.5 9.5.5 9.5.5s7.6 0 9.5-.5c1-.3 1.7-1 2-2 .5-1.9.5-5.8.5-5.8s0-3.9-.5-5.8zM9.6 15.6V8.4l6.3 3.6-6.3 3.6z"/></svg>
                YouTube पर देखें
              </a>
              <button onClick={() => setActive(null)} className="text-white hover:text-amber-300 flex items-center gap-1 text-sm" data-testid="byte-close">
                <X className="w-5 h-5"/> बंद करें
              </button>
            </div>
            <div className="aspect-video bg-black rounded-lg overflow-hidden">
              {ytId(active.video_url) ? (
                <iframe title={active.title} width="100%" height="100%"
                  src={`https://www.youtube.com/embed/${ytId(active.video_url)}?autoplay=1`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen/>
              ) : (
                <video src={active.video_url} controls autoPlay className="w-full h-full"/>
              )}
            </div>
            <div className="mt-3 text-white">
              <div className="font-devnagri-heading text-xl font-black">{active.title}</div>
              {active.description && <p className="text-slate-300 text-sm mt-1">{active.description}</p>}
              <p className="mt-2 text-[11px] text-amber-300/80">
                💡 अगर वीडियो यहाँ नहीं चलता, तो YouTube Studio में उस वीडियो के लिए <b>"Allow embedding"</b> चालू करें, या ऊपर <b>"YouTube पर देखें"</b> बटन दबाएँ।
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
